import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  FaTachometerAlt, FaSyncAlt, FaFileInvoiceDollar, FaFileInvoice, FaQuoteRight,
  FaExclamationTriangle, FaDollarSign, FaChevronLeft, FaChevronRight, FaSms, FaEnvelope,
  FaHandHoldingUsd, FaExclamationCircle, FaTimesCircle, FaUserSlash, FaChartLine,
} from 'react-icons/fa';
import { useAppConfig } from '../context/AppConfigContext';
import { getDashboardStats, refreshDashboardStats } from '../services/dashboardService';
import { listFranchiseesMissingXeroContact } from '../services/franchiseeService';
import MissingXeroContactsModal from '../components/MissingXeroContactsModal';
import JobStatusDonut from '../components/charts/JobStatusDonut';
import WeeklyActivityBar from '../components/charts/WeeklyActivityBar';
import RecentActivityFeed from '../components/RecentActivityFeed';
import QuickLinks from '../components/QuickLinks';
import DashboardSwitch from '../components/DashboardSwitch';

const WEEKS_BACK = 8; // the current week plus eight prior ones; never a future week

// Native Date week maths, matching ReportsPage.js — date-fns is not installed in this app.
const mondayOf = (d) => {
  const x = new Date(d);
  const day = x.getDay();
  x.setDate(x.getDate() - (day === 0 ? 6 : day - 1));
  x.setHours(0, 0, 0, 0);
  return x;
};

const toIsoDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const shortDate = (d) => d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });

const buildWeekOptions = () => {
  const currentMonday = mondayOf(new Date());
  const options = [];
  for (let i = 0; i <= WEEKS_BACK; i++) {
    const monday = new Date(currentMonday);
    monday.setDate(currentMonday.getDate() - i * 7);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    const range = `${shortDate(monday)} – ${shortDate(sunday)}`;
    options.push({ value: toIsoDate(monday), range, label: i === 0 ? `This week (${range})` : range });
  }
  return options;
};

// Status colours come from the runtime Firestore config, so the donut matches the JobsListPage pills.
const STATUS_HEX = {
  yellow: '#F59E0B', cyan: '#06B6D4', blue: '#3B82F6', green: '#10B981',
  orange: '#F97316', red: '#EF4444', gray: '#9CA3AF', gold: '#FBBF24',
};
const FALLBACK_HEX = ['#0E7490', '#06B6D4', '#10B981', '#F59E0B', '#EF4444', '#6366F1', '#9CA3AF'];

const HEADLINE_KEYS = ['invoices', 'rctis', 'invoiceValue', 'rctiValue', 'profit'];
const JOB_KEYS = ['quotes', 'overdue'];
const TODAY_KEYS = ['failedInvoices', 'failedRctis', 'sms', 'emails'];

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

const relativeWhen = (iso) => {
  if (!iso) return 'recently';
  const then = new Date(iso);
  if (isNaN(then.getTime())) return 'recently';
  const minutes = Math.round((Date.now() - then.getTime()) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  return `on ${then.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}`;
};

const absoluteWhen = (iso) => {
  if (!iso) return '';
  const then = new Date(iso);
  return isNaN(then.getTime()) ? '' : then.toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short', hour12: true });
};

const useDashboardStats = (weekStart) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  // Latest-wins: week switches and refreshes race, and a late response must never overwrite a newer week's.
  const requestId = useRef(0);

  const load = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const result = await getDashboardStats(weekStart);
      if (id !== requestId.current) return;
      setData(result);
    } catch (err) {
      if (id !== requestId.current) return;
      setData(null); // never leave the previous week's figures rendered under the error banner
      setError(err.message || 'Failed to load dashboard.');
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [weekStart]);

  // Recompute + snapshot server-side; the current figures stay on screen while it runs. They belong to
  // the same week, so a failed refresh keeps them — unlike a failed load, which has nothing valid to show.
  const refresh = useCallback(async () => {
    const id = ++requestId.current;
    setRefreshing(true);
    setError(null);
    try {
      const result = await refreshDashboardStats(weekStart);
      if (id !== requestId.current) return;
      setData(result);
    } catch (err) {
      if (id !== requestId.current) return;
      setError(err.message || 'Failed to refresh dashboard.');
    } finally {
      setRefreshing(false); // unconditional: only one refresh can be in flight, so this can never strand the button
    }
  }, [weekStart]);

  useEffect(() => { load(); }, [load]);

  return { data, loading, refreshing, error, refresh, reload: load };
};

const Dashboard = () => {
  const { getStatusByCode } = useAppConfig();
  const weekOptions = useMemo(buildWeekOptions, []);
  const [weekStart, setWeekStart] = useState(() => weekOptions[0].value);

  const { data, loading, refreshing, error, refresh, reload } = useDashboardStats(weekStart);

  // Not week-scoped: active franchisees with no linked Xero contact, for the stat card and its modal.
  const [missingXero, setMissingXero] = useState({ count: 0, franchisees: [], loading: true });
  const [missingXeroOpen, setMissingXeroOpen] = useState(false);

  const loadMissingXero = useCallback(async () => {
    try {
      const result = await listFranchiseesMissingXeroContact();
      setMissingXero({ count: result?.count || 0, franchisees: result?.franchisees || [], loading: false });
    } catch (err) {
      console.error('Failed to load franchisees without a Xero contact:', err);
      setMissingXero((prev) => ({ ...prev, loading: false }));
    }
  }, []);

  useEffect(() => { loadMissingXero(); }, [loadMissingXero]);

  const weekIndex = weekOptions.findIndex((o) => o.value === weekStart);
  const selectedWeek = weekIndex >= 0 ? weekOptions[weekIndex] : weekOptions[0];
  const stepWeek = (delta) => {
    const next = weekOptions[weekIndex + delta];
    if (next) setWeekStart(next.value);
  };

  const dailyBreakdown = Array.isArray(data?.dailyBreakdown) ? data.dailyBreakdown : [];

  const donutData = useMemo(() => (
    Object.entries(data?.jobStatusCounts || {})
      .filter(([, v]) => num(v) > 0)
      .sort((a, b) => num(b[1]) - num(a[1]))
      .map(([code, value], i) => {
        const cfg = getStatusByCode(code);
        return {
          name: cfg?.display || code,
          value: num(value),
          color: STATUS_HEX[cfg?.color?.toLowerCase()] || FALLBACK_HEX[i % FALLBACK_HEX.length],
        };
      })
  ), [data, getStatusByCode]);

  const overdueCount = num(data?.overdueJobCount);
  const quotesUnavailable = data?.quotesUnavailable === true;

  // Profit = invoice value minus RCTI value, both ex GST. Green when it is a profit, red when the
  // RCTI side is the larger, black at exactly zero. The ticket wants the amount and the percentage
  // together; the API gives -100% for RCTIs with no invoices and 0% for an empty week. The dash is
  // only for an API that predates profitPercent.
  const profitKnown = data?.profitExGst !== undefined && data?.profitExGst !== null;
  const profit = num(data?.profitExGst);
  const profitPercentKnown = data?.profitPercent !== null && data?.profitPercent !== undefined;
  const profitValue = profitKnown
    ? `${profit.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 })} (${profitPercentKnown ? `${Number(data.profitPercent).toLocaleString('en-AU', { maximumFractionDigits: 1 })}%` : '–%'})`
    : '—';
  const profitValueClass = profitKnown
    ? `text-lg lg:text-xl ${profit > 0 ? 'text-green-600' : profit < 0 ? 'text-red-600' : 'text-gray-800'}`
    : 'text-lg lg:text-xl text-gray-400';

  const kpiCards = [
    { key: 'invoices', label: 'Invoices this week', value: num(data?.invoiceCount).toLocaleString('en-AU'), icon: FaFileInvoiceDollar, tintBg: 'bg-cyan-100', tintText: 'text-jims-cyan-dark', valueClass: 'text-xl lg:text-2xl text-gray-800' },
    { key: 'rctis', label: 'RCTIs this week', value: num(data?.rctiCount).toLocaleString('en-AU'), icon: FaFileInvoice, tintBg: 'bg-indigo-100', tintText: 'text-indigo-600', valueClass: 'text-xl lg:text-2xl text-gray-800' },
    { key: 'invoiceValue', label: 'Invoice value (ex GST)', value: num(data?.totalInvoiceAmountExGst).toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }), icon: FaDollarSign, tintBg: 'bg-cyan-100', tintText: 'text-jims-cyan-dark', valueClass: 'text-lg lg:text-xl text-gray-800' },
    // The money paid out against the week, sitting beside Invoice value which is the money billed for it.
    // Two separate figures over the same week — never add them together or net them off; the Profit card
    // below is the one deliberate exception, showing the difference the ticket asked for.
    {
      key: 'rctiValue',
      label: 'RCTI value (ex GST)',
      value: num(data?.totalRctiAmountExGst).toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }),
      hint: 'Total value of RCTIs raised during this week, excluding GST. Invoice value is the invoice side of the same week.',
      icon: FaHandHoldingUsd, tintBg: 'bg-indigo-100', tintText: 'text-indigo-600',
      valueClass: 'text-lg lg:text-xl text-gray-800',
    },
    {
      key: 'profit',
      label: 'Profit (ex GST)',
      value: profitValue,
      hint: `${profitKnown && num(data?.totalInvoiceAmountExGst) === 0 && profit < 0 ? 'No invoices this week, so the whole RCTI value is a loss (-100%). ' : ''}Invoice value minus RCTI value for this week, both excluding GST. The percentage is the profit as a share of invoice value. Green is a profit, red is a loss.`,
      icon: FaChartLine, tintBg: 'bg-green-100', tintText: 'text-green-600',
      valueClass: profitValueClass,
    },
    // Not "quotes received": nothing records when a quote arrived, so this counts open quotes last touched
    // in the week — approved ones drop out, and unrelated edits pull old ones in.
    {
      key: 'quotes',
      label: 'Open quotes touched',
      value: quotesUnavailable ? '—' : num(data?.quotesReceived).toLocaleString('en-AU'),
      hint: quotesUnavailable
        ? 'Unavailable — this figure needs a Firestore index that has not been created yet.'
        : 'Jobs still awaiting quote approval that were updated during this week. Not a count of quotes received, and not comparable across weeks.',
      icon: FaQuoteRight, tintBg: 'bg-yellow-100', tintText: 'text-yellow-600',
      valueClass: `text-2xl ${quotesUnavailable ? 'text-gray-400' : 'text-gray-800'}`,
    },
    // Week-scoped like the other cards: jobs DUE in this week that are still outstanding, not the backlog.
    {
      key: 'overdue',
      label: 'Overdue this week',
      value: overdueCount.toLocaleString('en-AU'),
      hint: 'Jobs due during this week that are still outstanding. See Reports for the full overdue backlog.',
      icon: FaExclamationTriangle, tintBg: 'bg-red-100', tintText: 'text-red-600',
      valueClass: `text-2xl ${overdueCount > 0 ? 'text-red-600' : 'text-gray-800'}`,
    },
    // Failed generation is scoped to a day, like the SMS and email cards below: it answers "what broke
    // that I still have to re-run", which is a today question, not a property of the selected week.
    // A successful manual regeneration clears the job's error status, so these fall back to zero on their own.
    {
      key: 'failedInvoices',
      label: 'Failed invoices today',
      value: data?.failedInvoicesToday === null || data?.failedInvoicesToday === undefined ? '—' : num(data.failedInvoicesToday).toLocaleString('en-AU'),
      hint: 'Jobs whose invoice failed to generate today. Regenerating one successfully takes it off this count.',
      icon: FaExclamationCircle, tintBg: 'bg-red-100', tintText: 'text-red-600',
      valueClass: `text-2xl ${num(data?.failedInvoicesToday) > 0 ? 'text-red-600' : 'text-gray-800'}`,
    },
    {
      key: 'failedRctis',
      label: 'Failed RCTIs today',
      value: data?.failedRctisToday === null || data?.failedRctisToday === undefined ? '—' : num(data.failedRctisToday).toLocaleString('en-AU'),
      hint: 'Jobs whose RCTI failed to generate today. Regenerating one successfully takes it off this count.',
      icon: FaTimesCircle, tintBg: 'bg-red-100', tintText: 'text-red-600',
      valueClass: `text-2xl ${num(data?.failedRctisToday) > 0 ? 'text-red-600' : 'text-gray-800'}`,
    },
    // Day-scoped rather than week-scoped (ticket 77), so they say "today" on their face — otherwise
    // they would read as part of whichever week is on screen.
    {
      key: 'sms',
      label: 'SMS received today',
      value: data?.smsReceivedToday === null || data?.smsReceivedToday === undefined ? '—' : num(data.smsReceivedToday).toLocaleString('en-AU'),
      hint: data?.smsReceivedToday === null
        ? 'Unavailable — this figure needs a Firestore index that has not been created yet.'
        : 'Replies from franchisees received today, across every SMS conversation.',
      icon: FaSms, tintBg: 'bg-teal-100', tintText: 'text-teal-600',
      valueClass: `text-2xl ${data?.smsReceivedToday === null ? 'text-gray-400' : 'text-gray-800'}`,
    },
    {
      key: 'emails',
      label: 'Emails received today',
      value: data?.emailsReceivedToday === null || data?.emailsReceivedToday === undefined ? '—' : num(data.emailsReceivedToday).toLocaleString('en-AU'),
      hint: 'Support email picked up by the inbox monitor today. Internal mail is not counted.',
      icon: FaEnvelope, tintBg: 'bg-purple-100', tintText: 'text-purple-600',
      valueClass: `text-2xl ${data?.emailsReceivedToday === null ? 'text-gray-400' : 'text-gray-800'}`,
    },
  ];

  // Grouped by what the figure is scoped to, so a week change visibly moves only the week groups.
  const cardsFor = (keys) => keys.map((k) => kpiCards.find((c) => c.key === k)).filter(Boolean);
  const groupHeading = 'text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2';
  const compactCard = (card) => (
    <div key={card.key} className="bg-white rounded-lg shadow-md p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-gray-500 text-xs font-medium leading-tight" title={card.hint || undefined}>{card.label}</p>
          <p className={`font-bold tabular-nums leading-tight ${card.valueClass}`} title={card.hint || undefined}>{card.value}</p>
        </div>
        <div className={`${card.tintBg} rounded-full p-2 shrink-0`}>
          <card.icon className={`${card.tintText} text-base`} aria-hidden="true" />
        </div>
      </div>
    </div>
  );

  // The server resolves the week in Australia/Sydney, so its range wins over the browser's local one.
  const weekRangeLabel = (data?.weekStart && data?.weekEnd)
    ? `${shortDate(new Date(`${data.weekStart}T12:00:00`))} – ${shortDate(new Date(`${data.weekEnd}T12:00:00`))}`
    : selectedWeek.range;

  // A week change refetches while the previous week's figures are still on screen — say so.
  const freshnessLabel = (loading || refreshing)
    ? 'Updating figures…'
    : data?.isPrecomputed
      ? `Last updated ${relativeWhen(data?.generatedAt)}`
      : 'Live figures — not yet snapshotted';

  if (loading && !data) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="flex justify-center items-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-jims-cyan mx-auto mb-4"></div>
              <p className="text-gray-600">Loading dashboard&hellip;</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <div>
          <h1 className="text-3xl font-bold text-jims-cyan-dark flex items-center">
            <FaTachometerAlt className="mr-3" aria-hidden="true" />Dashboard
          </h1>
          <p role="status" aria-live="polite" className="text-sm text-gray-500 mt-1" title={data?.isPrecomputed ? absoluteWhen(data?.generatedAt) : ''}>
            {freshnessLabel}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => stepWeek(1)}
            disabled={weekIndex >= weekOptions.length - 1}
            aria-label="Previous week"
            className="p-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-jims-cyan disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <FaChevronLeft />
          </button>

          <label htmlFor="dashboard-week" className="sr-only">Week</label>
          <select
            id="dashboard-week"
            value={weekStart}
            onChange={(e) => setWeekStart(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-jims-cyan focus:border-jims-cyan"
          >
            {weekOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>

          <button
            type="button"
            onClick={() => stepWeek(-1)}
            disabled={weekIndex <= 0}
            aria-label="Next week"
            className="p-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-jims-cyan disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <FaChevronRight />
          </button>

          <button
            type="button"
            onClick={refresh}
            disabled={refreshing || loading}
            aria-busy={refreshing}
            className="flex items-center px-4 py-2 bg-jims-cyan text-white rounded-md hover:bg-jims-cyan-dark focus-visible:ring-2 focus-visible:ring-jims-cyan-dark focus-visible:ring-offset-2 disabled:opacity-50 text-sm font-medium"
          >
            {refreshing
              ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
              : <FaSyncAlt className="mr-2" aria-hidden="true" />}
            Refresh
          </button>
        </div>
      </div>

      {/* Sits under the header rather than among the week controls: it changes which dashboard you
          get, not anything about the figures on this one. */}
      <div className="mb-6">
        <DashboardSwitch current="new" />
      </div>

      {error && (
        <div role="alert" className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded">
          <p className="font-bold">Error</p>
          <p>{error}</p>
          <button
            type="button"
            onClick={reload}
            className="mt-2 px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 focus-visible:ring-2 focus-visible:ring-red-700 focus-visible:ring-offset-2 text-sm font-medium"
          >
            Retry
          </button>
        </div>
      )}

      {data?.truncated === true && (
        <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-800 p-4 mb-6 rounded">
          <p className="font-bold">Partial data</p>
          <p>Some figures hit the query cap and are a minimum, not an exact count.</p>
        </div>
      )}

      {!data && !error ? (
        <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">
          <p>No dashboard figures available for this week.</p>
        </div>
      ) : (
        <div aria-busy={loading || refreshing} className={`transition-opacity ${loading || refreshing ? 'opacity-60' : ''}`}>
          <div className="space-y-4 mb-6">
            <section aria-labelledby="kpi-week">
              <h2 id="kpi-week" className={groupHeading}>This week</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                {cardsFor(HEADLINE_KEYS).map((card) => (
                  <div key={card.key} className="bg-white rounded-lg shadow-md p-3">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-gray-600 text-xs font-medium leading-tight" title={card.hint || undefined}>{card.label}</p>
                      <div className={`${card.tintBg} rounded-full p-1.5 shrink-0`}>
                        <card.icon className={`${card.tintText} text-sm`} aria-hidden="true" />
                      </div>
                    </div>
                    <p className={`mt-2 font-bold tabular-nums leading-tight ${card.valueClass}`} title={card.hint || undefined}>{card.value}</p>
                  </div>
                ))}
              </div>
            </section>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
              <section aria-labelledby="kpi-jobs">
                <h2 id="kpi-jobs" className={groupHeading}>Jobs this week</h2>
                <div className="grid grid-cols-2 gap-3">{cardsFor(JOB_KEYS).map(compactCard)}</div>
              </section>
              <section aria-labelledby="kpi-today" className="xl:col-span-2">
                <h2 id="kpi-today" className={groupHeading}>Today</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{cardsFor(TODAY_KEYS).map(compactCard)}</div>
              </section>
            </div>
          </div>

          <section aria-labelledby="kpi-xero" className="mb-6">
            <h2 id="kpi-xero" className={groupHeading}>Franchisees</h2>
            <button
              type="button"
              onClick={() => setMissingXeroOpen(true)}
              className="bg-white rounded-lg shadow-md p-4 text-left w-full max-w-xs hover:shadow-lg focus-visible:ring-2 focus-visible:ring-jims-cyan"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-gray-600 text-sm font-medium leading-tight">Not in Xero</p>
                  <p className={`mt-2 text-2xl font-bold tabular-nums leading-tight ${missingXero.count > 0 ? 'text-red-600' : 'text-gray-800'}`}>
                    {missingXero.loading ? '—' : missingXero.count.toLocaleString('en-AU')}
                  </p>
                </div>
                <div className="bg-red-100 rounded-full p-2 shrink-0">
                  <FaUserSlash className="text-red-600 text-base" aria-hidden="true" />
                </div>
              </div>
              <p className="mt-2 text-xs text-gray-500">Active franchisees with no Xero contact. Click to view and create them.</p>
            </button>
          </section>

          <QuickLinks />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Job status &mdash; week of {weekRangeLabel}</h2>
              <JobStatusDonut data={donutData} />
            </div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Completed vs overdue</h2>
              <WeeklyActivityBar data={dailyBreakdown} />
            </div>
          </div>

          <RecentActivityFeed entries={data?.recentActivity || []} />
        </div>
      )}

      <MissingXeroContactsModal
        isOpen={missingXeroOpen}
        onClose={() => setMissingXeroOpen(false)}
        franchisees={missingXero.franchisees}
        onCompleted={loadMissingXero}
      />
    </div>
  );
};

export default Dashboard;
